import { UniqueIdentifier } from "@dnd-kit/core";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import PackagesIcon from "@redotech/redo-icons/packages.svg";
import PackagesFilledIcon from "@redotech/redo-icons/packages_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import {
  ConsolidationViewCountsContext,
  ConsolidationViewsContext,
  useConsolidationsEnabled,
} from "@redotech/redo-merchant-fulfillment-app/wms/consolidation/consolidation-views";
import { memo, useContext, useMemo } from "react";
import { useIsChildRouteActive } from "../orders/use-child-route-active";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const ConsolidationsNavItem = memo(function ConsolidationsNavItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const wmsEnabled = useCapability("wms");
  const viewsContext = useContext(ConsolidationViewsContext);
  const countsContext = useContext(ConsolidationViewCountsContext);
  const enabled = useConsolidationsEnabled() && wmsEnabled;
  const viewsBaseUrl = `${routes.consolidationsHref}/views`;
  const isAnyChildActive = useIsChildRouteActive(viewsBaseUrl, []);

  const allViews = useMemo(
    () => viewsContext?.views ?? [],
    [viewsContext?.views],
  );
  const allFolders = useMemo(
    () => viewsContext?.folders ?? [],
    [viewsContext?.folders],
  );
  const sortableItems = useMemo(() => {
    const foldersWithViews: FolderWithView<"consolidations">[] = allFolders.map(
      (folder) => ({
        folder,
        views: allViews.filter((view) => view.folderId === folder._id),
      }),
    );
    return viewsAndFoldersToTree({
      folderCounts: countsContext?.folderCounts,
      foldersWithViews,
      hideZeroNotifications: true,
      urlPrefix: viewsBaseUrl,
      viewCounts: countsContext?.viewCounts,
      viewsWithoutFolders: allViews.filter((view) => !view.folderId),
    });
  }, [allFolders, allViews, countsContext, viewsBaseUrl]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    viewsContext?.openEditFolderModal(id.toString());
  });
  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const { views, folders } = treeToViewsAndFolders<"consolidations">(
      flattenTree(items),
      allViews,
      allFolders,
    );
    viewsContext?.updateOrdering(views, folders);
  });

  if (!enabled) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isAnyChildActive}
      FilledIcon={PackagesFilledIcon}
      friendlyName={t("Consolidations", {
        context:
          "shipping navigation label for consolidating multiple shipments or packages",
      })}
      id="consolidations"
      UnfilledIcon={PackagesIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: `${viewsBaseUrl}/create` }}
        friendlyName={t("+ Add new view", {
          context:
            "order, fulfillment, claims, and warehouse operations navigation label",
        })}
        id="consolidations-create-view"
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => viewsContext?.openCreateFolderModal(),
        }}
        friendlyName={t("+ Add new folder", {
          context:
            "order, fulfillment, claims, and warehouse operations navigation label",
        })}
        id="consolidations-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
